import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import okReservaLogo from '../../assets/okreserva-logo.png';
import { toast } from 'sonner';
import axios from 'axios';
import { useClientStore } from '../../store/useStore';
import { Delete, ArrowRight, User, Eye, EyeOff, AtSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const { nickname: urlNickname } = useParams();
  const navigate = useNavigate();
  const { clientLogin, isClientAuthenticated, requiresPinChange, client } = useClientStore();
  
  // Two steps: 1) Enter nickname, 2) Enter PIN
  const hasValidNickname = urlNickname && urlNickname !== 'demo' && urlNickname.length > 0;
  const [step, setStep] = useState(hasValidNickname ? 'pin' : 'nickname');
  const [nickname, setNickname] = useState(hasValidNickname ? urlNickname : '');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  
  // Ref to prevent double submissions
  const isSubmittingRef = useRef(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isClientAuthenticated && client) {
      // Si el cliente ya cambió su PIN, ir directo al dashboard
      if (client.pin_changed) {
        navigate(`/clientes/${client.nickname}/dashboard`);
      } else if (requiresPinChange) {
        navigate(`/clientes/${client.nickname}/cambiar-pin`);
      } else {
        navigate(`/clientes/${client.nickname}/dashboard`);
      }
    }
  }, [isClientAuthenticated, requiresPinChange, navigate, client]);

  const handleNicknameSubmit = (e) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError('Ingresa tu nickname');
      return;
    }
    setError('');
    setStep('pin');
    // Update URL to include nickname
    navigate(`/clientes/${nickname.toLowerCase()}`, { replace: true });
  };

  const handleKeyPress = useCallback((num) => {
    if (pin.length < 4) {
      setPin(prev => prev + num);
      setError('');
    }
  }, [pin]);

  const handleDelete = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  }, []);

  const handleClear = useCallback(() => {
    setPin('');
    setError('');
  }, []);

  const handleLogin = async () => {
    if (pin.length < 4) {
      setError('El PIN debe tener 4 dígitos');
      return;
    }

    // Prevent double submissions using ref (works with StrictMode)
    if (isSubmittingRef.current || loading) return;
    isSubmittingRef.current = true;

    setLoading(true);
    setError('');
    
    try {
      // Use axios instead of fetch to avoid body stream issues
      const response = await axios.post(`${API_URL}/api/clients/auth/login`, {
        nickname: nickname.toLowerCase(),
        pin
      });

      const data = response.data;

      clientLogin(data.client, data.access_token);
      
      if (!data.client.pin_changed) {
        toast.info('Debes cambiar tu PIN por seguridad');
        navigate(`/clientes/${data.client.nickname}/cambiar-pin`);
      } else {
        toast.success(`¡Bienvenido, ${data.client.name}!`);
        navigate(`/clientes/${data.client.nickname}/dashboard`);
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error al iniciar sesión';
      setError(errorMessage);
      setPin('');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleBack = () => {
    setStep('nickname');
    setPin('');
    setError('');
  };

  // Keyboard support for PIN
  useEffect(() => {
    if (step !== 'pin') return;
    
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      } else if (e.key === 'Enter' && pin.length === 4) {
        handleLogin();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, handleKeyPress, handleDelete, handleClear, pin]);

  const numpadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'delete']
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img
              src={okReservaLogo}
              alt="OkReserva"
              className="h-10 w-auto mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Portal de Clientes
          </h1>
          {step === 'pin' && (
            <p className="text-slate-400 text-sm">
              @{nickname}
            </p>
          )}
        </div>

        {/* Step 1: Enter Nickname */}
        {step === 'nickname' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 shadow-2xl">
            <form onSubmit={handleNicknameSubmit}>
              <label className="text-slate-400 text-sm mb-2 block text-center">
                Ingresa tu nickname
              </label>
              <div className="relative mb-4">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <AtSign className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => {
                    setNickname(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                    setError('');
                  }}
                  placeholder="tu_nickname"
                  autoFocus
                  className="w-full pl-12 pr-4 py-4 bg-slate-700/50 border-2 border-slate-600 rounded-xl text-white text-lg placeholder-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              {error && (
                <p className="text-red-400 text-sm text-center mb-4">{error}</p>
              )}
              <button
                type="submit"
                disabled={!nickname.trim()}
                className={`w-full h-14 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-200 ${
                  nickname.trim()
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 active:scale-[0.98] shadow-lg shadow-orange-500/30'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }`}
              >
                Continuar
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Enter PIN */}
        {step === 'pin' && (
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 shadow-2xl">
            {/* Back button */}
            <button
              onClick={handleBack}
              className="text-slate-400 text-sm mb-4 flex items-center gap-1 hover:text-white transition-colors"
            >
              ← Cambiar nickname
            </button>
            
            {/* PIN Display */}
            <div className="mb-6">
              <label className="text-slate-400 text-sm mb-2 block text-center">
                Ingresa tu PIN
              </label>
              <div className="flex items-center justify-center gap-2 relative">
                <div className="flex gap-3">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all duration-200 ${
                        i < pin.length
                          ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                          : 'border-slate-600 bg-slate-700/50 text-slate-500'
                      }`}
                    >
                      {i < pin.length ? (showPin ? pin[i] : '•') : ''}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-0 p-2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {error && (
                <p className="text-red-400 text-sm text-center mt-2">{error}</p>
              )}
            </div>

            {/* Numpad */}
            <div className="grid gap-3">
              {numpadButtons.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-3 gap-3">
                  {row.map((btn) => {
                    if (btn === 'clear') {
                      return (
                        <button
                          key={btn}
                          onClick={handleClear}
                          className="h-14 rounded-xl bg-slate-700/50 text-slate-400 text-sm font-medium hover:bg-slate-600/50 active:scale-95 transition-all duration-150"
                        >
                          Borrar
                        </button>
                      );
                    }
                    if (btn === 'delete') {
                      return (
                        <button
                          key={btn}
                          onClick={handleDelete}
                          className="h-14 rounded-xl bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 active:scale-95 transition-all duration-150 flex items-center justify-center"
                        >
                          <Delete className="w-6 h-6" />
                        </button>
                      );
                    }
                    return (
                      <button
                        key={btn}
                        onClick={() => handleKeyPress(btn)}
                        className="h-14 rounded-xl bg-slate-700/80 text-white text-2xl font-semibold hover:bg-slate-600/80 active:scale-95 transition-all duration-150"
                      >
                        {btn}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={pin.length < 4 || loading}
              className={`w-full mt-6 h-14 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-200 ${
                pin.length >= 4 && !loading
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 active:scale-[0.98] shadow-lg shadow-orange-500/30'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Ingresar
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Powered by <span className="text-orange-400">OkReserva</span>
        </p>
      </div>
    </div>
  );
}
